import { useEffect, useState, useRef } from "react";
import { Globe, Check } from "lucide-react";

// Popular languages — covers ~95% of StudyNet's global client base.
const LANGS = [
  { code: "en", label: "English" },
  { code: "zh-CN", label: "中文 (Simplified)" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "ne", label: "नेपाली" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "ko", label: "한국어" },
  { code: "ja", label: "日本語" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "th", label: "ไทย" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
];

let scriptInjected = false;
function ensureGoogleTranslate() {
  if (scriptInjected || typeof window === "undefined") return;
  scriptInjected = true;

  window.googleTranslateElementInit = function () {
    // eslint-disable-next-line no-undef
    new google.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: LANGS.map((l) => l.code).join(","),
        autoDisplay: false,
        layout: 0,
      },
      "google_translate_element"
    );
  };

  const s = document.createElement("script");
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

function readCookieLang() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/googtrans=\/[a-z-]+\/([a-zA-Z-]+)/);
  return m ? m[1] : "en";
}

function setTranslateCookie(code) {
  const host = window.location.hostname;
  const root = host.split(".").slice(-2).join(".");
  const value = code === "en" ? "" : `/en/${code}`;
  // Clear all variants then set fresh on every relevant scope
  const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=;${expire};path=/`;
  document.cookie = `googtrans=;${expire};path=/;domain=${host}`;
  document.cookie = `googtrans=;${expire};path=/;domain=.${host}`;
  document.cookie = `googtrans=;${expire};path=/;domain=.${root}`;
  if (value) {
    document.cookie = `googtrans=${value};path=/`;
    document.cookie = `googtrans=${value};path=/;domain=${host}`;
    document.cookie = `googtrans=${value};path=/;domain=.${host}`;
    document.cookie = `googtrans=${value};path=/;domain=.${root}`;
  }
}

export default function LanguageSwitcher({ mobile = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const ref = useRef(null);

  useEffect(() => {
    ensureGoogleTranslate();
    setCurrent(readCookieLang());
  }, []);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = (code) => {
    setCurrent(code);
    setTranslateCookie(code);
    setOpen(false);
    window.location.reload();
  };

  const currentLabel = (LANGS.find((l) => l.code === current) || LANGS[0]).label;

  return (
    <div className="relative notranslate" ref={ref} translate="no">
      {/* Google Translate mount point — kept off-screen so the widget initialises but stays invisible */}
      <div
        id="google_translate_element"
        aria-hidden
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: 1,
          height: 1,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      <button
        type="button"
        data-testid="language-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 ${mobile ? "px-2 py-2 text-xs" : "px-3 py-2 text-sm"} text-[#29324A] hover:text-[#5A3A8B] transition-colors font-medium`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Choose language"
      >
        <Globe className={mobile ? "h-4 w-4" : "h-4 w-4"} />
        {!mobile && <span className="hidden xl:inline text-[12px]">{currentLabel}</span>}
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          className={`absolute ${mobile ? "right-0" : "right-0"} top-full mt-1 w-56 bg-white border border-slate-200 shadow-xl py-1.5 z-50 max-h-[60vh] overflow-y-auto`}
        >
          <div className="px-3 py-1.5 text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase border-b border-slate-100 mb-1">
            Choose language
          </div>
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={current === l.code}
              data-testid={`language-option-${l.code}`}
              onClick={() => select(l.code)}
              className={`w-full text-left px-3 py-2 text-[13px] flex items-center justify-between gap-2 hover:bg-[#F8F4FB] transition-colors ${
                current === l.code ? "text-[#29324A] bg-[#F8F4FB] font-semibold" : "text-[#29324A]"
              }`}
            >
              <span>{l.label}</span>
              {current === l.code && <Check className="h-3.5 w-3.5 text-[#C43A89]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
